/**
 * 页面中文文案提取工具。
 *
 * 与 VC 端巡检逻辑保持一致：启动后先全量扫描，再通过 MutationObserver
 * 持续收集动态渲染的文本和常见文案属性，外部在路由变化后取增量上报。
 */

const CHINESE_REGEX = /[\u4e00-\u9fa5]+/g;
const ATTRS_TO_CHECK = ['placeholder', 'title', 'alt', 'aria-label', 'label'];
const SKIP_NODE_NAMES = ['SCRIPT', 'STYLE', 'NOSCRIPT', 'LINK', 'META'];

const collectedTexts = new Set<string>();
const lastReportedTexts = new Set<string>();
let observer: MutationObserver | null = null;

function extractAndStore(text: string | null | undefined) {
  if (!text) {
    return;
  }

  const matches = text.match(CHINESE_REGEX);
  if (!matches) {
    return;
  }

  matches.forEach(match => {
    if (!collectedTexts.has(match)) {
      collectedTexts.add(match);
      //console.log('%c[Found Chinese]', 'color: #1890ff; font-weight: bold;', match);
    }
  });
}

function scanNode(node: Node) {
  if (SKIP_NODE_NAMES.includes(node.nodeName)) {
    return;
  }

  if (node.nodeType === Node.TEXT_NODE) {
    if (node.nodeValue && node.nodeValue.trim()) {
      extractAndStore(node.nodeValue);
    }
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const element = node as HTMLElement;
  ATTRS_TO_CHECK.forEach(attr => {
    const value = element.getAttribute(attr);
    if (value) {
      extractAndStore(value);
    }
  });

  if (element.tagName === 'INPUT') {
    const inputElement = element as HTMLInputElement;
    if (['button', 'submit', 'reset'].includes(inputElement.type)) {
      extractAndStore(inputElement.value);
    }
  }

  node.childNodes.forEach(child => scanNode(child));
}

export function startChineseScanner() {
  if (observer) {
    console.warn('Chinese scanner is already running.');
    return;
  }

  if (!document.body) {
    return;
  }

  console.log('%c[Chinese Scanner] Started...', 'color: green; font-weight: bold;');
  scanNode(document.body);

  observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(scanNode);
      }

      if (mutation.type === 'characterData') {
        extractAndStore(mutation.target.nodeValue);
      }

      if (mutation.type === 'attributes') {
        const attrName = mutation.attributeName;
        if (attrName && ATTRS_TO_CHECK.includes(attrName)) {
          extractAndStore((mutation.target as HTMLElement).getAttribute(attrName));
        }
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ATTRS_TO_CHECK,
  });
}

export function stopChineseScanner() {
  if (!observer) {
    return;
  }

  observer.disconnect();
  observer = null;
  console.log('%c[Chinese Scanner] Stopped.', 'color: red; font-weight: bold;');
}

export function getCollectedChineseTexts(): string[] {
  return Array.from(collectedTexts);
}

export function getNewTextsAndSync(): string[] {
  const newTexts: string[] = [];
  collectedTexts.forEach(text => {
    if (!lastReportedTexts.has(text)) {
      newTexts.push(text);
      lastReportedTexts.add(text);
    }
  });
  return newTexts;
}

export function clearCollectedTexts() {
  collectedTexts.clear();
  lastReportedTexts.clear();
}

export default {
  start: startChineseScanner,
  stop: stopChineseScanner,
  get: getCollectedChineseTexts,
  getNewAndSync: getNewTextsAndSync,
  clear: clearCollectedTexts,
};
